/**
 * Where the field Befunde list was left off: the search term and the scroll
 * offset, per project. Held in sessionStorage rather than component state because
 * the list unmounts as soon as another field category is opened, and holding it
 * in memory alone would still lose it to a reload — routine on a tablet the
 * system suspends. Session, not local: a filter that survived closing the app
 * would silently hide Befunde on the next launch.
 */

import { defineStoredJson } from '../../utils/storage';

export interface FieldFindingsListState {
    /** The quick-filter term the list was filtered by. */
    search: string;
    /** The list's scroll offset in px. */
    scrollTop: number;
}

type Store = Record<string, FieldFindingsListState>;

// Renamed from the unprefixed `fieldFindingsList` when every key in the app
// moved to one prefix; what a tab is holding is carried over on the first read.
const listStates = defineStoredJson<Store>({
    key: 'field_findings_list',
    area: 'session',
    legacyKeys: ['fieldFindingsList'],
    fallback: () => ({}),
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Store)
            : undefined
});

function emptyState(): FieldFindingsListState {
    return { search: '', scrollTop: 0 };
}

function readAll(): Store {
    return listStates.read();
}

function writeAll(store: Store): void {
    // Storage disabled or full — the list simply opens at the top next time.
    listStates.write(store);
}

/** Where the list for one project was left (a fresh, empty state when unknown). */
export function loadFindingsListState(
    projectId: string
): FieldFindingsListState {
    return readAll()[projectId] ?? emptyState();
}

/** Update one project's remembered list state, leaving the other half as it was. */
export function saveFindingsListState(
    projectId: string,
    patch: Partial<FieldFindingsListState>
): void {
    const store = readAll();
    store[projectId] = { ...(store[projectId] ?? emptyState()), ...patch };
    writeAll(store);
}
